import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import Button from "components/button";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditTripDaysThresholdForm from "./useEditTripDaysThresholdForm";

export const editTripDaysThresholdPageRouteName = (id?: string) =>
  `/config/trip-days-threshold/${id ?? ":id"}/edit`;

const EditTripDaysThresholdPage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    handleSubmit,
    errors,
    register,
    control,
    durationCondition,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  } = useEditTripDaysThresholdForm(id);

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          {/* Row: Name + Duration Condition + Equivalent Days */}
          <div className="grid grid-cols-3 gap-4">
            {/* Trip Days Name */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-trip-days-name")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                placeholder={t("label-trip-days-name")}
                required
                {...register("tripDaysName")}
                error={errors.tripDaysName}
                data-testid="input-trip-days-threshold-name-edit"
              />
            </div>

            {/* Duration Condition (Hours) */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-duration-condition")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <div className="flex items-start gap-2">
                <div className="w-[120px] shrink-0">
                  <Controller
                    name="durationCondition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={[
                          { label: t("condition-below"), value: "below" },
                          { label: t("condition-between"), value: "between" },
                          { label: t("condition-above"), value: "above" },
                        ]}
                        value={field.value}
                        onValueChange={(val) => field.onChange(val)}
                        data-testid="dropdown-duration-condition-threshold-edit"
                      />
                    )}
                  />
                </div>

                {durationCondition === "between" ? (
                  <div className="flex items-start gap-2 flex-1">
                    <Input
                      enableNumberFormat
                      type="number"
                      placeholder="0"
                      {...register("durationMin")}
                      error={errors.durationMin}
                      data-testid="input-duration-min-threshold-edit"
                    />
                    <div className="flex items-center gap-2 shrink-0 pt-2.5">
                      <span className="text-xs font-medium text-white bg-primary-500 rounded px-2 py-0.5">{t("label-duration-min")}</span>
                    </div>
                    <Input
                      enableNumberFormat
                      type="number"
                      placeholder="0"
                      {...register("durationMax")}
                      error={errors.durationMax}
                      data-testid="input-duration-max-threshold-edit"
                    />
                    <div className="flex items-center gap-2 shrink-0 pt-2.5">
                      <span className="text-xs font-medium text-white bg-primary-500 rounded px-2 py-0.5">{t("label-duration-max")}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1">
                    <Input
                      enableNumberFormat
                      type="number"
                      placeholder="0"
                      {...register("durationValue")}
                      error={errors.durationValue}
                      data-testid={durationCondition === "below" ? "input-duration-max-threshold-edit" : "input-duration-min-threshold-edit"}
                    />
                  </div>
                )}
              </div>
              <Typography variant="body" tone="muted" className="text-xs">
                {durationCondition === "below" && t("hint-below")}
                {durationCondition === "between" && t("hint-between")}
                {durationCondition === "above" && t("hint-above")}
              </Typography>
            </div>

            {/* Equivalent Days */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-equivalent-days")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                enableNumberFormat
                type="number"
                placeholder="0"
                {...register("equivalentDays")}
                error={errors.equivalentDays}
                data-testid="input-equivalent-days-threshold-edit"
              />
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              {t("label-description")}
            </Typography>
            <Input
              placeholder={t("label-description")}
              {...register("description")}
              error={errors.description}
              data-testid="textarea-threshold-description-edit"
            />
          </div>
        </form>
      </section>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-threshold-edit"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSubmit(onSubmit)}
          data-testid="btn-save-threshold-edit"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-threshold-edit-modal"
      />

      {/* Discard Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="discard-threshold-edit-modal"
      />
    </div>
  );
};

export default EditTripDaysThresholdPage;
